import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cx } from '../../ui';
import { Swipe } from '../../ui';
import { heroUnits } from '../../heroUnits';
import { Check, Stage, reduced } from './kit';

// Ramp's "systems that never spoke", told for one unit (the first hero unit, a heat pump): scattered fragments joined by dashed lines
// resolve, on scroll, into one clean unit record. Each fragment flies into the row it becomes.

const clamp = (v: number, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const ease = (t: number) => 1 - Math.pow(1 - t, 3);

const paper = { boxShadow: '0 18px 34px -22px rgba(24,19,67,.45), 0 0 0 1px rgba(36,30,97,.10)' } as const;

const FragCard = () => (
  <div className="rounded-[6px] p-3.5" style={{ ...paper, background: '#FBF7EC' }}>
    <div className="flex items-center justify-between border-b border-dashed border-[#C9BFA4] pb-1.5">
      <span className="text-[10px] font-extrabold tracking-[0.1em] text-[#5B5240]">WARRANTY CARD</span>
      <span className="font-mono text-[8.5px] text-[#8C8167]">POST WITHIN 14 DAYS</span>
    </div>
    {['Name', 'Serial no.', 'Date of purchase', 'Installer'].map((f, i) => (
      <div key={f} className="mt-2 flex items-end gap-2 text-[9.5px] text-[#6C634F]">
        <span className="shrink-0">{f}</span>
        <span className="relative h-[12px] flex-1 border-b border-[#BDB297]">
          {i === 0 && <span className="absolute -top-[3px] left-1 text-[11px] italic text-[#2D3A80]" style={{ fontFamily: 'cursive' }}>J. Okafor</span>}
          {i === 1 && <span className="absolute -top-[3px] left-1 text-[11px] italic text-[#2D3A80]" style={{ fontFamily: 'cursive' }}>KAW9-26-?</span>}
        </span>
      </div>
    ))}
  </div>
);

const FragEmail = () => (
  <div className="rounded-[10px] bg-white p-3" style={paper}>
    <div className="flex items-center gap-2">
      <span className="grid h-6 w-6 place-items-center rounded-full bg-[#E8E7F3] text-[9px] font-bold text-[#4C4970]">BH</span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[10.5px] font-semibold text-[#1C1B22]">RE: RE: RE: serial number?</div>
        <div className="truncate text-[9px] text-[#8A88A8]">service@ · 14 messages</div>
      </div>
    </div>
    <p className="mt-2 text-[10px] leading-[1.45] text-[#4A4766]">Can the homeowner send a photo of the label? We can&rsquo;t find this unit in the warranty list.</p>
    <span className="mt-2 inline-block rounded-full bg-[#FDECEC] px-2 py-[2px] text-[9px] font-medium text-[#B23B3B]">Proof of purchase missing</span>
  </div>
);

const FragSheet = () => (
  <div className="overflow-hidden rounded-[8px] bg-white" style={paper}>
    <div className="flex items-center gap-1.5 bg-[#1F6E43] px-2.5 py-1.5 text-[9.5px] font-medium text-white">
      <span className="grid h-3.5 w-3.5 place-items-center rounded-[2px] bg-white text-[8px] font-bold text-[#1F6E43]">X</span>
      warranty_reg_FINAL(3).xlsx
    </div>
    <div className="grid grid-cols-[1.3fr_1fr_.9fr] font-mono text-[8.5px] text-[#4A4766]">
      {['SERIAL', 'OWNER', 'START', '..00398', 'Mill Close', '03/06/25', '..00409', '?', '#N/A', '..00417', '', '', '..00411', 'see email', '??'].map((c, i) => (
        <span key={i} className={cx('border-b border-r border-[#E6E6EE] px-1.5 py-[3px]', i < 3 && 'bg-[#F4F4F8] text-[#8A88A8]', c === '#N/A' && 'bg-[#FDECEC] text-[#B23B3B]', c === '..00417' && 'bg-[#FFF6D6]')}>
          {c || ' '}
        </span>
      ))}
    </div>
  </div>
);

const FragFax = () => (
  <div className="rounded-[4px] p-3" style={{ ...paper, background: '#F3F2EE', filter: 'contrast(1.05)' }}>
    <div className="flex justify-between font-mono text-[8.5px] text-[#6E6C66]">
      <span>SCAN · INSTALLER</span>
      <span>P 1/3</span>
    </div>
    <div className="mt-1.5 text-[11px] font-bold tracking-[0.04em] text-[#3B3A36]">COMMISSIONING SHEET</div>
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="mt-1.5 flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 border border-[#77756E]">{i < 2 && <span className="block text-center text-[8px] leading-[8px] text-[#3B3A36]">x</span>}</span>
        <span className="h-[5px] flex-1 rounded bg-[#D6D4CD]" style={{ maxWidth: `${80 - i * 12}%` }} />
      </div>
    ))}
    <div className="mt-2 text-[8.5px] italic text-[#8B8981]">signature illegible</div>
  </div>
);

const FragPdf = () => (
  <div className="flex gap-2.5 rounded-[10px] bg-white p-3" style={paper}>
    <span className="grid h-12 w-10 shrink-0 place-items-center rounded-[4px] bg-[#C93B3B] text-[9px] font-bold text-white">PDF</span>
    <div className="min-w-0">
      <div className="truncate text-[10.5px] font-semibold text-[#1C1B22]">AW9_install_manual_rev_A.pdf</div>
      <div className="text-[9px] text-[#8A88A8]">96 pages · 2023</div>
      <div className="mt-1.5 rounded-[6px] bg-[#FFF6D6] px-1.5 py-[3px] text-[9px] text-[#7A5B00]">Which revision is this unit?</div>
    </div>
  </div>
);

const FragNote = () => (
  <div className="p-3" style={{ background: '#FFE98A', boxShadow: '0 16px 30px -20px rgba(0,0,0,.45)' }}>
    <div className="text-[11.5px] leading-[1.35] text-[#3D3510]" style={{ fontFamily: 'cursive' }}>
      Who owns 00417 now?
      <br />
      House sold in May?
      <br />
      Ring the installer.
    </div>
  </div>
);

type Frag = { key: string; el: React.ReactNode; wide: [number, number, number]; narrow: [number, number, number]; drift: string };
const FRAGS: Frag[] = [
  { key: 'sheet', el: <FragSheet />, wide: [20, 300, 3], narrow: [6, 404, 3], drift: '4.1s' },
  { key: 'note', el: <FragNote />, wide: [870, 330, 5], narrow: [214, 420, 5], drift: '3.7s' },
  { key: 'card', el: <FragCard />, wide: [40, 26, -5], narrow: [0, 10, -5], drift: '4.6s' },
  { key: 'fax', el: <FragFax />, wide: [860, 40, -4], narrow: [220, 20, 4], drift: '4.3s' },
  { key: 'pdf', el: <FragPdf />, wide: [440, 360, -2], narrow: [100, 260, -3], drift: '3.9s' },
  { key: 'email', el: <FragEmail />, wide: [450, 30, 3], narrow: [110, 130, 2], drift: '4.8s' },
];

const UNIT = heroUnits[0];
const ROWS = [
  { k: 'Serial', v: UNIT.serial, s: 'From your ERP', mono: true },
  { k: 'Owner', v: UNIT.owner, s: 'Since 19 Oct 2026' },
  { k: 'Warranty', v: 'Active', s: `Started at ${UNIT.handoverName.toLowerCase()}` },
  { k: UNIT.partnerRole === 'installer' ? 'Installer' : 'Partner', v: UNIT.partner, s: `${UNIT.handoverName} record, sealed PDF` },
  { k: 'Manual', v: `${UNIT.model}, rev C`, s: 'The right one for this unit' },
  { k: 'Service', v: 'Case 2207', s: `Photos attached, routed to ${UNIT.partnerRole}` },
];

// line pairs (indices into FRAGS)
const LINKS: [number, number][] = [
  [2, 5],
  [5, 3],
  [3, 1],
  [1, 4],
  [4, 0],
  [0, 2],
  [5, 4],
];

// Product thumbnail: photography only. Catalogue shot when it exists, else a crop of the commissioning photo.
function UnitThumb() {
  const [src, setSrc] = useState(UNIT.product);
  const fallback = src !== UNIT.product;
  return (
    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-[12px] bg-[#F1F0F7]">
      <img
        src={src}
        alt=""
        width={48}
        height={48}
        className="block h-full w-full object-cover"
        style={fallback ? { objectPosition: '97% 50%', transform: 'scale(2.4)', transformOrigin: '97% 50%' } : undefined}
        onError={() => !fallback && setSrc('/images/v2/gen/heatpump-commission-b-thumb.jpg')}
      />
    </div>
  );
}

function Scene({ p, narrow }: { p: number; narrow: boolean }) {
  const W = narrow ? 380 : 1100;
  const FW = 230;
  const fs = narrow ? 0.66 : 1; // fragment base scale
  const card = narrow ? { x: 10, y: 40, w: 360, head: 84, row: 50 } : { x: 330, y: 22, w: 440, head: 92, row: 54 };
  const cardIn = ease(clamp((p - 0.22) / 0.12));
  const linesOut = clamp(1 - (p - 0.12) / 0.16);
  const done = p > 0.86;

  const centre = (f: Frag) => {
    const [x, y] = narrow ? f.narrow : f.wide;
    return [x + (FW * fs) / 2, y + 50 * fs];
  };

  return (
    <div className="relative" style={{ width: W, height: narrow ? 580 : 560 }}>
      <svg className="absolute inset-0" width={W} height={narrow ? 580 : 560} aria-hidden="true" style={{ opacity: linesOut }}>
        {LINKS.map(([a, b]) => {
          const [x1, y1] = centre(FRAGS[a]);
          const [x2, y2] = centre(FRAGS[b]);
          const mx = (x1 + x2) / 2 + (y2 - y1) * 0.18;
          const my = (y1 + y2) / 2 - (x2 - x1) * 0.12;
          return <path key={`${a}-${b}`} d={`M${x1} ${y1} Q${mx} ${my} ${x2} ${y2}`} fill="none" stroke="#A9A6C9" strokeWidth="1.4" strokeDasharray="4 6" />;
        })}
      </svg>

      {/* the record */}
      <div
        className="absolute overflow-hidden rounded-[18px] bg-white"
        style={{
          left: card.x,
          top: card.y,
          width: card.w,
          opacity: cardIn,
          transform: `translateY(${(1 - cardIn) * 16}px) scale(${0.97 + cardIn * 0.03})`,
          boxShadow: '0 40px 80px -40px rgba(24,19,67,.5), 0 0 0 1px #E1E0EE',
        }}
      >
        <div className="flex items-center gap-3 border-b border-[#ECEBF4] px-5" style={{ height: card.head }}>
          <UnitThumb />
          <div className="min-w-0 flex-1">
            <div className="text-[16px] font-bold leading-tight text-[#1C1B22]">{UNIT.model} {UNIT.noun}</div>
            {!narrow && <div className="mt-0.5 font-mono text-[10.5px] text-[#7B789D]">One record · one code on the plate</div>}
          </div>
          <span className={cx('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-medium transition-colors duration-200', done ? 'bg-[#E3F5E9] text-[#1F7A43]' : 'bg-[#F1F0F7] text-[#6F6C8E]')}>
            <span className={cx('h-[6px] w-[6px] rounded-full', done ? 'bg-[#3FAE6C]' : 'bg-[#BDBBD4]')} />
            {done ? 'Complete' : 'Building'}
          </span>
        </div>
        {ROWS.map((r, i) => {
          const t = clamp((p - (0.3 + i * 0.075)) / 0.2);
          const on = clamp((t - 0.55) / 0.45);
          return (
            <div key={r.k} className="flex items-center gap-3 border-b border-[#F0EFF6] px-5 last:border-0" style={{ height: card.row }}>
              <span className="w-[72px] shrink-0 font-mono text-[10px] uppercase tracking-[0.08em] text-[#9A98B5]">{r.k}</span>
              <span className="min-w-0 flex-1" style={{ opacity: on, transform: `translateX(${(1 - on) * 8}px)` }}>
                <span className={cx('block truncate text-[13px] font-semibold text-[#1C1B22]', r.mono && 'font-mono text-[12px]')}>{r.v}</span>
                <span className="block truncate text-[11px] text-[#7B789D]">{r.s}</span>
              </span>
              <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full transition-colors duration-200" style={{ background: on > 0.9 ? '#2F9E58' : '#F1F0F7', color: '#fff' }}>
                {on > 0.9 && <Check size={11} />}
              </span>
            </div>
          );
        })}
      </div>

      {/* who reads the record (final state, wide only) */}
      {!narrow &&
        [
          { x: 40, y: 120, k: 'Owner scans', v: 'Right manual, genuine parts, help', side: 'l' },
          { x: 40, y: 300, k: 'Installer scans', v: 'Service jobs, commissioning history', side: 'l' },
          { x: 800, y: 120, k: 'Your team', v: 'Cases, claims, recalls by serial', side: 'r' },
          { x: 800, y: 300, k: 'Your ERP', v: 'Registrations and returns written back', side: 'r' },
        ].map((c, i) => {
          const o = ease(clamp((p - 0.8 - i * 0.025) / 0.1));
          return (
            <div key={c.k} className="absolute flex w-[260px] items-center gap-3" style={{ left: c.x, top: c.y, opacity: o, transform: `translateX(${(1 - o) * (c.side === 'l' ? -10 : 10)}px)`, flexDirection: c.side === 'l' ? 'row' : 'row-reverse' }}>
              <div className="flex-1 rounded-[12px] bg-white px-3.5 py-2.5 shadow-[0_16px_30px_-20px_rgba(24,19,67,.45)] ring-1 ring-[#E4E3F0]" style={{ textAlign: c.side === 'l' ? 'right' : 'left' }}>
                <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#2F9E58]">{c.k}</div>
                <div className="mt-0.5 text-[12.5px] font-medium leading-snug text-[#232047]">{c.v}</div>
              </div>
              <span className="h-px w-8 border-t border-dashed border-[#A9A6C9]" />
            </div>
          );
        })}

      {/* the fragments */}
      {FRAGS.map((f, i) => {
        const [x, y, r] = narrow ? f.narrow : f.wide;
        const row = i; // FRAGS and ROWS share an order
        const t = ease(clamp((p - (0.3 + row * 0.075)) / 0.2));
        const tx = card.x + 60;
        const ty = card.y + card.head + row * card.row - 20;
        const sc = lerp(fs, 0.28, t);
        return (
          <div
            key={f.key}
            className={cx('vb-frag', p < 0.05 && 'vb-frag-drift')}
            style={
              {
                left: 0,
                top: 0,
                width: FW,
                transformOrigin: 'top left',
                transform: `translate(${lerp(x, tx, t)}px, ${lerp(y, ty, t)}px) rotate(${lerp(r, 0, t)}deg) scale(${sc})`,
                opacity: t < 0.6 ? 1 : 1 - (t - 0.6) / 0.4,
                zIndex: 5 + i,
                '--d': f.drift,
              } as React.CSSProperties
            }
            aria-hidden="true"
          >
            {f.el}
          </div>
        );
      })}
    </div>
  );
}

export function BeforeAfter() {
  const ref = useRef<HTMLElement>(null);
  const [p, setP] = useState(0);
  const [narrow, setNarrow] = useState(false);
  const [still] = useState(reduced);

  useLayoutEffect(() => {
    const fit = () => setNarrow(window.innerWidth < 760);
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  useEffect(() => {
    if (still) return;
    let raf = 0;
    const on = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const span = r.height - window.innerHeight;
        setP(clamp(-r.top / (span || 1)));
      });
    };
    on();
    window.addEventListener('scroll', on, { passive: true });
    window.addEventListener('resize', on);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', on);
      window.removeEventListener('resize', on);
    };
  }, [still]);

  const after = still || p > 0.42;
  const prog = still ? 1 : p;

  return (
    <section ref={ref} className="relative bg-bm-alt" style={{ height: still ? 'auto' : '290vh' }} aria-labelledby="vb-ba-h">
      <div className={cx(!still && 'sticky top-[68px] flex h-[calc(100vh-68px)] flex-col justify-center overflow-hidden', 'py-10')}>
        <div className="wrap w-full">
          <div className="grid items-end gap-4 md:grid-cols-[1fr_auto]">
            <div className="relative min-h-[118px] md:min-h-[120px]">
              <h2 id="vb-ba-h" className="sr-only">
                Today a unit&rsquo;s record lives in six places. With BrandedMark it lives on the unit.
              </h2>
              <div aria-hidden="true" className="vb-cut absolute inset-x-0 bottom-0" style={{ opacity: after ? 0 : 1, transform: after ? 'translateY(-8px)' : 'none' }}>
                <div className="vb-label mb-3">Today</div>
                <div className="vb-h2 max-w-[760px]">
                  One unit. Six places to look. <span className="vb-two">None of them talk to each other.</span>
                </div>
              </div>
              <div aria-hidden="true" className="vb-cut absolute inset-x-0 bottom-0" style={{ opacity: after ? 1 : 0, transform: after ? 'none' : 'translateY(8px)' }}>
                <div className="vb-label mb-3 !text-bm-emerald">With BrandedMark</div>
                <div className="vb-h2 max-w-[760px]">
                  One record, <Swipe>on the unit.</Swipe> <span className="vb-two">Every scan adds to it.</span>
                </div>
              </div>
            </div>
            <div className="hidden w-[180px] md:block" aria-hidden="true">
              <div className="h-[3px] w-full overflow-hidden rounded-full bg-[#E1E0EE]">
                <div className="h-full bg-bm-indigo" style={{ width: `${prog * 100}%` }} />
              </div>
              <div className="mt-2 flex justify-between font-mono text-[11px] text-bm-muted">
                <span>Before</span>
                <span>After</span>
              </div>
            </div>
          </div>
          <div className="mt-6 md:mt-8">
            <Stage w={narrow ? 380 : 1100} h={narrow ? 580 : 560} label="Illustration: a paper warranty card, an email chain asking for a serial number, a spreadsheet with missing owners, a scanned commissioning sheet, an old PDF manual and a sticky note asking who owns the unit now, resolving into one BrandedMark unit record with serial, owner, warranty, installer, manual and service case.">
              <Scene p={prog} narrow={narrow} />
            </Stage>
          </div>
        </div>
      </div>
    </section>
  );
}
